import { useEffect, useState } from "react";
import api from "@/lib/api";
import { STATUS_META } from "@/components/StatusBadge";
import { Link, Navigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
} from "recharts";
import { ArrowUpRight, TrendingUp, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { canSeeAmount } from "@/lib/roles";
import usePageTitle from "@/hooks/use-page-title";

const CARDS = [
  { key: "today_orders", label: "Today's Orders" },
  { key: "pending_orders", label: "Pending Orders" },
  { key: "design_pending", label: "Design Pending" },
  { key: "printing_pending", label: "Printing Pending" },
  { key: "ready_dispatch", label: "Ready for Dispatch" },
  { key: "delivered", label: "Delivered" },
  { key: "cancelled", label: "Cancelled" },
  { key: "total_orders", label: "Total Orders" },
];

const MONEY_CARDS = [
  { key: "today_collection", label: "Today's Collection", prefix: "₹" },
  { key: "monthly_sales", label: "Monthly Sales", prefix: "₹" },
  { key: "outstanding", label: "Outstanding", prefix: "₹" },
];

export default function Dashboard() {
  usePageTitle("Dashboard");
  const { user } = useAuth();
  const showAmount = canSeeAmount(user);
  const [stats, setStats] = useState(null);
  const [shopOrders, setShopOrders] = useState([]);

  useEffect(() => {
    if (user?.role === "customer") return;
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/shop/orders").then((r) => setShopOrders(r.data.slice(0, 5))).catch(() => setShopOrders([]));
  }, [user]);

  // Customers do not see the internal dashboard
  if (user?.role === "customer") return <Navigate to="/portal" replace />;

  if (!stats)
    return <div className="text-sm text-muted-foreground">Loading dashboard…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <div className="flex items-end justify-between border-b border-[hsl(var(--border))] pb-6">
        <div>
          <div className="overline mb-2">Overview</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">
            Dashboard
          </h1>
        </div>
        <Link to="/orders/new" className="btn-swiss btn-accent" data-testid="quick-new-order">
          + New Order
        </Link>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-grid">
        {CARDS.map((c) => (
          <div
            key={c.key}
            className="card-swiss p-4 hover:-translate-y-0.5 transition-transform"
            data-testid={`kpi-${c.key}`}
          >
            <div className="overline">{c.label}</div>
            <div className="font-display font-black text-3xl mt-2">
              {stats[c.key] ?? 0}
            </div>
          </div>
        ))}
      </div>

      {/* Money cards */}
      {showAmount && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {MONEY_CARDS.map((c) => (
          <div key={c.key} className="card-swiss p-6" data-testid={`money-${c.key}`}>
            <div className="flex items-center justify-between">
              <div className="overline">{c.label}</div>
              <TrendingUp size={14} className="text-[#FF6B00]" />
            </div>
            <div className="font-display font-black text-4xl mt-3">
              {c.prefix}
              {Math.round(stats[c.key] || 0).toLocaleString("en-IN")}
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Charts */}
      {showAmount && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-swiss p-5 lg:col-span-2" data-testid="revenue-chart-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Revenue · Last 7 days</div>
              <div className="font-display font-bold text-xl mt-1">Collections</div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.revenue_graph}>
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 2,
                    fontSize: 12,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke="#FF6B00"
                  strokeWidth={2}
                  dot={{ fill: "#FF6B00", r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-swiss p-5" data-testid="status-breakdown-card">
          <div className="overline mb-4">Orders by status</div>
          <div className="space-y-2">
            {Object.entries(stats.status_breakdown)
              .filter(([, v]) => v > 0)
              .map(([k, v]) => {
                const meta = STATUS_META[k] || { label: k, color: "#71717A" };
                return (
                  <div key={k} className="flex items-center gap-3 text-sm">
                    <span className="status-dot" style={{ background: meta.color }} />
                    <span className="flex-1 capitalize">{meta.label}</span>
                    <span className="font-mono font-bold">{v}</span>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
      )}

      {/* Top customers & products */}
      {showAmount && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-swiss p-5" data-testid="top-customers-card">
          <div className="overline mb-4">Top Customers</div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.top_customers} layout="vertical">
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  width={120}
                  stroke="hsl(var(--muted-foreground))"
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 2,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="amount" fill="#FF6B00" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-swiss p-5" data-testid="top-products-card">
          <div className="overline mb-4">Top Products</div>
          <div className="divide-y divide-[hsl(var(--border))]">
            {stats.top_products.map((p, i) => (
              <div key={p.name} className="flex items-center py-3">
                <span className="font-mono text-xs w-8 text-muted-foreground">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex-1 text-sm">{p.name}</span>
                <span className="font-mono font-bold">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      {/* Recent orders */}
      <div className="card-swiss" data-testid="recent-orders-card">
        <div className="p-5 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div className="overline">Recent Orders</div>
          <Link to="/orders" className="text-xs font-semibold hover:text-[#FF6B00] flex items-center gap-1">
            View all <ArrowUpRight size={12} />
          </Link>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[hsl(var(--border))]">
              <th className="px-5 py-3 text-left overline">Order</th>
              <th className="px-5 py-3 text-left overline">Customer</th>
              <th className="px-5 py-3 text-left overline">Product</th>
              {showAmount && <th className="px-5 py-3 text-right overline">Amount</th>}
              <th className="px-5 py-3 text-left overline">Status</th>
            </tr>
          </thead>
          <tbody>
            {stats.recent_orders.map((o) => {
              const meta = STATUS_META[o.status] || {};
              return (
                <tr
                  key={o.id}
                  className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
                >
                  <td className="px-5 py-3">
                    <Link
                      to={`/orders/${o.id}`}
                      className="font-mono text-xs hover:text-[#FF6B00]"
                    >
                      {o.id}
                    </Link>
                  </td>
                  <td className="px-5 py-3">{o.customer_name}</td>
                  <td className="px-5 py-3 text-muted-foreground">{o.product_name}</td>
                  {showAmount && (
                    <td className="px-5 py-3 text-right font-mono">
                      ₹{o.estimated_cost?.toLocaleString("en-IN")}
                    </td>
                  )}
                  <td className="px-5 py-3">
                    <span
                      className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider"
                      style={{ color: meta.color }}
                    >
                      <span className="status-dot" style={{ background: meta.color }} />
                      {meta.label || o.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Online Shop Orders */}
      <div className="card-swiss" data-testid="dashboard-shop-orders">
        <div className="p-5 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div className="overline flex items-center gap-2">
            <ShoppingBag size={14} className="text-[#FF6B00]" /> Online Shop Orders
          </div>
          <Link to="/shop-orders" className="text-xs font-semibold hover:text-[#FF6B00] flex items-center gap-1">
            View all <ArrowUpRight size={12} />
          </Link>
        </div>
        {shopOrders.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground">No online orders yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[hsl(var(--border))]">
                <th className="px-5 py-3 text-left overline">Order</th>
                <th className="px-5 py-3 text-left overline">Customer</th>
                <th className="px-5 py-3 text-left overline">Items</th>
                {showAmount && <th className="px-5 py-3 text-right overline">Total</th>}
                <th className="px-5 py-3 text-left overline">Payment</th>
                <th className="px-5 py-3 text-left overline">Status</th>
              </tr>
            </thead>
            <tbody>
              {shopOrders.map((o) => (
                <tr key={o.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]">
                  <td className="px-5 py-3 font-mono text-xs">{o.order_no}</td>
                  <td className="px-5 py-3">
                    <div>{o.address?.full_name}</div>
                    <div className="text-[10px] text-muted-foreground">{o.address?.phone}</div>
                  </td>
                  <td className="px-5 py-3">{o.items?.length}</td>
                  {showAmount && <td className="px-5 py-3 text-right font-mono">₹{Math.round(o.total).toLocaleString("en-IN")}</td>}
                  <td className="px-5 py-3">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {(o.payment_status || "pending").toUpperCase()}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-xs font-bold capitalize text-[#FF6B00]">{(o.status || "").replace(/_/g, " ")}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
